import React from "react";
import {
  useUiStyles,
  useModuleBEM,
  IconNameEnum,
} from "@b2bportal/core-themes";
import { CoreUiComponents, type InputErrorType } from "@b2bportal/core-types";
import {
  InputContainer as CoreInputContainer,
  type InputContainerProps as CoreInputContainerProps,
  Icon,
} from "@b2bportal/core-ui";
import clsx from "clsx";
import defaultStyles from "./InputContainer.module.scss";

export type InputContainerProps = CoreInputContainerProps & {
  label: string;
  floatingLabel?: boolean;
};

const COMPONENT_KEY = CoreUiComponents.InputContainer;

export const InputContainer: React.FC<InputContainerProps> = ({
  id,
  label,
  className,
  startContent,
  endContent,
  children,
  hasValue = false,
  hasError = false,
  floatingLabel = true,
  disabled = false,
}) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY, {
    error: hasError,
    filled: hasValue,
    disabled: disabled,
  });

  return (
    <div
      className={clsx(block, className, {
        [cn("floating-label")]: floatingLabel,
      })}
    >
      {!floatingLabel ? (
        <label htmlFor={id} className={cn("label")}>
          {label}
        </label>
      ) : null}
      <CoreInputContainer
        id={id}
        className={className}
        startContent={startContent}
        endContent={endContent}
        hasValue={hasValue}
        hasError={hasError}
        disabled={disabled}
        {...(floatingLabel && { label })}
      >
        {children}
      </CoreInputContainer>
    </div>
  );
};

export const InputError: React.FC<{
  error?: InputErrorType;
}> = ({ error }) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);
  return error != null && error.message !== "" ? (
    <div className={cn("error")} role="alert">
      <Icon iconName={IconNameEnum.error} className={cn("error-icon")} />
      {error.message}
    </div>
  ) : null;
};
